import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { OwcIconButton, OwcListItem, OwcMenu } from "@one/react";
import { v4 } from "uuid";
import { getFieldValue, Notify, SubMenu } from "@digitallab/grid-common-frontend-components";
import { useHistory } from "react-router-dom";
import { APP_PATHS, entryType, equipmentStatus, DEFAULT_MYLAB_FILTER, MY_LAB, INSTRUMENTS_REPO_ENDPOINT } from "../../constants";
import { isAllowedAccessControlStatus } from "./accessControlStatus";
import { useSelector } from "react-redux";
import { RootState } from "../../reducers";
import {
  CREATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
  UPDATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
  GET_CHECKLIST_FOR_EQUIPMENT
} from "../../gql/logBooksapi";
import { react18WorkaroundProps } from "../../utils/constants/react18WorkaroundProps";
import { configureAwsAppSync } from "../../utils/helpers/configureAwsAppSync";
import { API } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";

const REPOSITORY_URL = process.env.REACT_APP_REPOSITORY_URL || "";
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";
const MONITORING_URL = process.env.REACT_APP_MONITORING_URL || "";

const getEntryTypeSuffix = (entryTypeValue: string) =>
  entryTypeValue === entryType?.cluster ? " cluster" : " equipment";

const MyLabMenuItem = ({
  onClose,
  isInMyLab,
  onToggleMyLab,
  isDisabled
}: {
  onClose: () => void;
  isInMyLab: boolean;
  onToggleMyLab: () => void;
  isDisabled?: boolean;
}) => (
  <OwcListItem
    data-testid="simple-menu-my-lab"
    disabled={isDisabled}
    onClick={() => {
      if (!isDisabled) {
        onClose();
        onToggleMyLab();
      }
    }}
    {...react18WorkaroundProps}
  >
    {isInMyLab ? "Remove from My lab" : "Add to My lab"}
  </OwcListItem>
);

const BookingActionsMenuItem = ({
  isDisabled,
  entryTypeValue,
  inventoryId
}: {
  isDisabled: boolean;
  entryTypeValue: string;
  inventoryId: string;
}) => (
  <SubMenu
    label="Booking action(s)"
    testId="simple-menu-booking-actions"
    submenuTestId="simple-menu-booking-submenu"
    itemCount={1}
  >
    {(close: () => void) => (
      <OwcListItem
        data-testid="simple-menu-book-equipment"
        disabled={isDisabled}
        onClick={() => {
          close();
          if (!isDisabled) {
            window.open(`${BOOKING_URL}/equipmentList/${inventoryId}?autoAdvance=true`, "_blank");
          }
        }}
        {...react18WorkaroundProps}
      >
        Book{getEntryTypeSuffix(entryTypeValue)}
      </OwcListItem>
    )}
  </SubMenu>
);

const RepositoryActionsMenuItem = ({
  entryTypeValue,
  inventoryId,
  isEditDisabled,
  isRemoveDisabled,
  isDocumentationDisabled
}: {
  entryTypeValue: string;
  inventoryId: string;
  isEditDisabled: boolean;
  isRemoveDisabled: boolean;
  isDocumentationDisabled: boolean;
}) => (
  <SubMenu
    label="Repository action(s)"
    testId="simple-menu-repository-actions"
    submenuTestId="simple-menu-repository-submenu"
    itemCount={5}
  >
    {(close: () => void) => {
      const openRepoAction = (action: string) => {
        close();
        window.open(`${REPOSITORY_URL}/equipmentList/${inventoryId}?action=${action}`, "_blank");
      };
      return (
        <>
          <OwcListItem
            data-testid="simple-menu-view-equipment"
            onClick={() => openRepoAction("view")}
            {...react18WorkaroundProps}
          >
            View details
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-edit-equipment"
            disabled={isEditDisabled}
            onClick={() => { if (!isEditDisabled) { openRepoAction("edit"); } }}
            {...react18WorkaroundProps}
          >
            Edit{getEntryTypeSuffix(entryTypeValue)}
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-remove-equipment"
            disabled={isRemoveDisabled}
            onClick={() => { if (!isRemoveDisabled) { openRepoAction("remove"); } }}
            {...react18WorkaroundProps}
          >
            Remove{getEntryTypeSuffix(entryTypeValue)}
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-view-audit-trail"
            onClick={() => openRepoAction("audit")}
            {...react18WorkaroundProps}
          >
            View audit trail
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-view-documentation"
            disabled={isDocumentationDisabled}
            onClick={() => { if (!isDocumentationDisabled) { openRepoAction("documentation"); } }}
            {...react18WorkaroundProps}
          >
            View documentation
          </OwcListItem>
        </>
      );
    }}
  </SubMenu>
);

const ViewInMonitoringMenuItem = ({
  onClose,
  inventoryId,
  isDisabled
}: {
  onClose: () => void;
  inventoryId: string;
  isDisabled: boolean;
}) => (
  <OwcListItem
    data-testid="simple-menu-view-monitoring"
    disabled={isDisabled}
    onClick={() => {
      if (!isDisabled) {
        onClose();
        window.open(`${MONITORING_URL}/${inventoryId}`, "_blank");
      }
    }}
    {...react18WorkaroundProps}
  >
    View in Monitoring
  </OwcListItem>
);

const checkUserGroupPermission = (irGroups: any[], params: any): boolean =>
  irGroups.some((group) => {
    const belongingToGroup = getFieldValue(params?.data, "belonging_to_group");
    return typeof belongingToGroup === "string" && belongingToGroup.startsWith(group.groupName);
  });

interface EquipmentActionsMenuProps {
  params: any;
  landingPageAppSyncClient: any;
  myLabFilter: any;
  onMyLabUpdated: () => void;
}

const EquipmentActionsMenu = ({
  params,
  landingPageAppSyncClient,
  myLabFilter,
  onMyLabUpdated
}: EquipmentActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState(false);
  const history = useHistory();
  const canEdit = useSelector((store: RootState) => store.user.canEdit);
  const canBook = useSelector((store: RootState) => store.user.canBook);
  const isBookingAdmin = useSelector((store: RootState) => store.user.isBookingAdmin);
  const storeUser = useSelector((store: RootState) => store.user);
  const irRole = useSelector((store: RootState) => store.user.irRole);
  const irGroups = useSelector((store: RootState) => store.user.irGroups);

  const menuId = v4();
  const itemId = getFieldValue(params?.data, "id")?.toString();
  const inventoryId = itemId;
  const entryTypeValue = getFieldValue(params?.data, "entry_type");
  const statusValue = getFieldValue(params?.data, "status");
  const isSubComponent =
    entryTypeValue === entryType?.clusterSubequipment || !!getFieldValue(params?.data, "cluster_id");
  const isVisualized = String(getFieldValue(params?.data, "is_visualized")).toLowerCase() === "true";
  const isBookable = String(getFieldValue(params?.data, "is_bookable")).toLowerCase() === "true";
  const isDeleted = statusValue === equipmentStatus.deleted.key;
  const isPending = statusValue === equipmentStatus.pending.key;
  const isStandaloneEquipment = entryTypeValue === entryType.standaloneEquipment;

  const [hasDocumentation, setHasDocumentation] = useState(false);
  const documentationChecked = useRef(false);

  useEffect(() => {
    if (documentationChecked.current) {
      return;
    }
    const equipmentModel = getFieldValue(params?.data, "equipment_model") || getFieldValue(params?.data, "equipmentModel");
    const siteName = getFieldValue(params?.data, "site_name") || getFieldValue(params?.data, "siteName");
    const belongingToGroup = getFieldValue(params?.data, "belonging_to_group") || getFieldValue(params?.data, "belongingToGroup");
    if (!equipmentModel || !siteName || !belongingToGroup) {
      return;
    }
    documentationChecked.current = true;
    const fetchDocumentation = async () => {
      try {
        configureAwsAppSync({ aws_appsync_graphqlEndpoint: INSTRUMENTS_REPO_ENDPOINT });
        const result = await API.graphql({
          query: GET_CHECKLIST_FOR_EQUIPMENT,
          variables: { input: { model: equipmentModel, site: siteName, group: belongingToGroup } }
        }) as GraphQLResult<any>;
        const items = result?.data?.getChecklistsForEquipment?.items || [];
        setHasDocumentation(items.length > 0);
      } catch {
        setHasDocumentation(false);
      }
    };
    fetchDocumentation();
  }, [params?.data]);

  const myLabInventoryIds = myLabFilter?.filterDefinition?.inventoryIds || [];
  const [localIsInMyLab, setLocalIsInMyLab] = useState(myLabInventoryIds.includes(inventoryId));
  const isBookingDisabled = !canBook || isSubComponent || !isBookable || isDeleted || (!isBookingAdmin && !isAllowedAccessControlStatus(params?.data, storeUser));
  const isLogActionDisabled =
    !canEdit ||
    isSubComponent ||
    (isStandaloneEquipment ? statusValue !== equipmentStatus.active.key : isDeleted);

  const hasIrPermission =
    irRole === "Admin" || (irRole === "User" && checkUserGroupPermission(irGroups || [], params));
  const isRepoEditDisabled = !hasIrPermission || isDeleted || isPending;
  const isRepoRemoveDisabled = !hasIrPermission || isDeleted || isPending;

  const handleMenuClose = () => setAnchorEl(false);
  const handleMenuItemClose = () => setAnchorEl(null as any);
  const handleMenuClick = () => setAnchorEl(true as any);

  const handleToggleMyLab = async () => {
    const filterDef = myLabFilter?.filterDefinition
      ? typeof myLabFilter.filterDefinition === "string"
        ? JSON.parse(myLabFilter.filterDefinition)
        : { ...myLabFilter.filterDefinition }
      : { ...DEFAULT_MYLAB_FILTER.filterDefinition };

    const currentIds = filterDef.inventoryIds || [];
    if (localIsInMyLab) {
      filterDef.inventoryIds = currentIds.filter((id: string) => id !== inventoryId);
    } else {
      filterDef.inventoryIds = [...currentIds, inventoryId];
    }

    const lpUserID = myLabFilter?.lpUserID;
    const hasExistingFilter = !!myLabFilter?.filterName;
    try {
      const result = await landingPageAppSyncClient.mutate({
        mutation: hasExistingFilter
          ? UPDATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER
          : CREATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
        variables: {
          userID: lpUserID,
          filterName: MY_LAB,
          filterDefinition: JSON.stringify(filterDef)
        }
      });

      if (result?.data?.updateUserFilter || result?.data?.createUserFilter) {
        setLocalIsInMyLab(!localIsInMyLab);
        Notify({
          title: "",
          text: "My lab configuration successfully saved",
          type: "success",
          icon: "circle_confirm"
        });
        onMyLabUpdated();
      }
    } catch (error: any) {
      Notify({
        title: "",
        text: `My lab configuration failed to save: ${error.message}`,
        type: "warning",
        icon: "alarm"
      });
    }
  };

  const handleViewLogbook = () => {
    handleMenuItemClose();
    window.open(`/cover-sheet/${inventoryId}`, "_blank");
  };



  const handleAddRunLog = () => {
    handleMenuItemClose();
    if (entryTypeValue === entryType?.standaloneEquipment) {
      history.push(`${APP_PATHS.RUN_LOG}?equipmentId=${inventoryId}`);
    } else {
      history.push(`${APP_PATHS.CLUSTER_RUN_LOG}?equipmentId=${inventoryId}`);
    }
  };

  const handleAddActionLog = () => {
    handleMenuItemClose();
    if (entryTypeValue === entryType?.standaloneEquipment) {
      history.push(`${APP_PATHS.ACTION_LOG}?equipmentId=${inventoryId}`);
    } else {
      history.push(`${APP_PATHS.CLUSTER_ACTION_LOG}?equipmentId=${inventoryId}`);
    }
  };

  if (!params?.data?.id || itemId === "dummyRow") {
    return null;
  }

  return (
    <>
      <OwcIconButton
        icon="more_vertical"
        type="legacy"
        flat
        onClick={handleMenuClick}
        id={menuId}
        {...react18WorkaroundProps}
      />
      {ReactDOM.createPortal(
        <OwcMenu
          data-testid="simple-menu"
          anchor={menuId}
          visible={anchorEl}
          onClickOutside={handleMenuClose}
          disablePortal={true}
          limit={10}
          {...react18WorkaroundProps}
        >
          <OwcListItem
            data-testid="simple-menu-view-logbook"
            onClick={handleViewLogbook}
            {...react18WorkaroundProps}
          >
            View Log book
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-add-run-log"
            disabled={isLogActionDisabled}
            onClick={handleAddRunLog}
            {...react18WorkaroundProps}
          >
            Add Run log(s)
          </OwcListItem>
          <OwcListItem
            data-testid="simple-menu-add-action-log"
            disabled={isLogActionDisabled}
            onClick={handleAddActionLog}
            {...react18WorkaroundProps}
          >
            Add Action log(s)
          </OwcListItem>
          {!isSubComponent && (
            <MyLabMenuItem onClose={handleMenuItemClose} isInMyLab={localIsInMyLab} onToggleMyLab={handleToggleMyLab} isDisabled={isDeleted} />
          )}
          <BookingActionsMenuItem
            isDisabled={isBookingDisabled}
            entryTypeValue={entryTypeValue}
            inventoryId={inventoryId}
          />
          <RepositoryActionsMenuItem
            entryTypeValue={entryTypeValue}
            inventoryId={inventoryId}
            isEditDisabled={isRepoEditDisabled}
            isRemoveDisabled={isRepoRemoveDisabled}
            isDocumentationDisabled={!hasDocumentation}
          />
          <ViewInMonitoringMenuItem
            onClose={handleMenuItemClose}
            inventoryId={inventoryId}
            isDisabled={!isVisualized || isDeleted}
          />
        </OwcMenu>,
        document.body
      )}
    </>
  );
};

export default EquipmentActionsMenu;
